import React, { useState, useEffect, useRef, Suspense } from 'react';

/**
 * LazySection - Intersection Observer based lazy loading wrapper
 * Only loads child component when it comes into viewport
 * 
 * @param {Object} props
 * @param {React.ComponentType} props.component - Component to lazy load
 * @param {Object} props.componentProps - Props to pass to the component
 * @param {number} props.rootMargin - Margin for intersection observer (default: '100px')
 * @param {number} props.threshold - Intersection threshold (default: 0.1)
 * @param {React.ReactNode} props.fallback - Loading fallback component
 * @param {Object} props.style - Inline styles for the wrapper
 * @param {string} props.className - CSS classes for the wrapper
 */
const LazySection = ({ 
    component: Component, 
    componentProps = {}, 
    rootMargin = '100px',
    threshold = 0.1,
    fallback = null,
    style = {},
    className = '',
    minHeight = null,
    estimatedHeight = null,
    ...wrapperProps
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const currentRef = ref.current;
        
        // Check if Intersection Observer is supported
        if (!window.IntersectionObserver || !currentRef) {
            // Fallback: load immediately if no support
            setIsVisible(true);
            setIsLoaded(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting && !isLoaded) {
                    setIsVisible(true);
                    setIsLoaded(true);
                    // Disconnect observer after loading to prevent re-triggering
                    observer.unobserve(currentRef);
                }
            },
            {
                rootMargin,
                threshold,
            }
        );

        observer.observe(currentRef);

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [rootMargin, threshold, isLoaded]);

    return (
        <div 
            ref={ref} 
            style={style} 
            className={className}
            {...wrapperProps}
        >
            {isVisible ? (
                <Suspense fallback={fallback}>
                    <Component {...componentProps} />
                </Suspense>
            ) : (
                // Proper placeholder to prevent CLS - reserve actual content space
                <div style={{ 
                    minHeight: minHeight || 'auto',
                    visibility: 'hidden',
                    height: estimatedHeight || 'auto'
                }}>
                    {fallback}
                </div>
            )}
        </div>
    );
};

/**
 * Higher-order component version for easier usage
 * @param {React.ComponentType} Component - Component to make lazy
 * @param {Object} defaultOptions - Default lazy loading options
 */
export const withLazyLoading = (Component, defaultOptions = {}) => {
    const LazyComponent = (props) => (
        <LazySection
            component={Component}
            componentProps={props}
            {...defaultOptions}
        />
    );
    
    LazyComponent.displayName = `withLazyLoading(${Component.displayName || Component.name || 'Component'})`;
    
    return LazyComponent;
};

export default LazySection;
